import {
  addCalendarDays,
  calendarDayKey,
  EVENT_LOAD_DAY_WINDOW,
  getEventCalendarDay,
  hasAggregateDaysAtOrAfter,
  startOfLocalDay,
} from '../../../services/dateUtils';

export function countLoadedByDay(events) {
  const counts = {};
  (Array.isArray(events) ? events : []).forEach((event) => {
    const day = getEventCalendarDay(event);
    if (!day) return;
    const key = calendarDayKey(day);
    if (!key) return;
    counts[key] = (counts[key] || 0) + 1;
  });
  return counts;
}

export function isDayComplete(dayKey, loadedByDay, byDay) {
  if (!dayKey) return false;
  const loaded = (loadedByDay && loadedByDay[dayKey]) || 0;
  const expected = (byDay && byDay[dayKey]) || 0;
  return loaded >= expected;
}

export function shouldGiveUpOnDay(dayKey, loadedByDay, byDay, repairedDays) {
  if (isDayComplete(dayKey, loadedByDay, byDay)) return false;
  return Boolean(repairedDays && repairedDays.has(dayKey));
}

export function incompleteDayKeys(loadedByDay, byDay, from, to) {
  const keys = [];
  if (!(from instanceof Date) || !(to instanceof Date)) return keys;
  for (let day = startOfLocalDay(from); day < to; day = addCalendarDays(day, 1)) {
    const key = calendarDayKey(day);
    const expected = (byDay && byDay[key]) || 0;
    const loaded = (loadedByDay && loadedByDay[key]) || 0;
    if (expected > loaded) keys.push(key);
  }
  return keys;
}

export function nextRange(loadedThrough, windowDays = EVENT_LOAD_DAY_WINDOW) {
  const start = startOfLocalDay(loadedThrough);
  return {
    start,
    end: addCalendarDays(start, windowDays || EVENT_LOAD_DAY_WINDOW),
  };
}

export function hasMoreFromAggregate(byDay, loadedThrough) {
  return hasAggregateDaysAtOrAfter(byDay, loadedThrough);
}

export function parseCalendarDayKey(dayKey) {
  if (!dayKey || typeof dayKey !== 'string') return null;
  const parts = dayKey.split('-').map(Number);
  if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) return null;
  const [year, month, day] = parts;
  return new Date(year, month - 1, day);
}

export function collectCompletedDayKeys(events, byDay, from, to, repairedDays) {
  const loadedByDay = countLoadedByDay(events);
  const completed = new Set();
  const markIfDone = (key) => {
    if (!key) return;
    if (isDayComplete(key, loadedByDay, byDay) || shouldGiveUpOnDay(key, loadedByDay, byDay, repairedDays)) {
      completed.add(key);
    }
  };

  if (from instanceof Date && to instanceof Date) {
    for (let day = startOfLocalDay(from); day < to; day = addCalendarDays(day, 1)) {
      markIfDone(calendarDayKey(day));
    }
  }

  Object.keys(loadedByDay).forEach((key) => markIfDone(key));
  return completed;
}

export function featuredDaysNeedingRepair(featured, loadedByDay, byDay) {
  const days = [];
  const seen = new Set();
  (Array.isArray(featured) ? featured : []).forEach((event) => {
    const day = getEventCalendarDay(event);
    if (!day) return;
    const key = calendarDayKey(day);
    if (!key || seen.has(key)) return;
    seen.add(key);
    if (!isDayComplete(key, loadedByDay, byDay)) {
      days.push(startOfLocalDay(day));
    }
  });
  return days;
}

export function appendUniqueById(prev, incoming) {
  const base = Array.isArray(prev) ? prev : [];
  if (!incoming || !incoming.length) return base;
  const seen = new Set(base.map((event) => event.id));
  const added = incoming.filter((event) => event?.id && !seen.has(event.id));
  return added.length ? [...base, ...added] : base;
}

export function prependUniqueById(featured, prev) {
  const base = Array.isArray(prev) ? prev : [];
  if (!featured || !featured.length) return base;
  const featuredIds = new Set(featured.map((event) => event.id));
  const rest = base.filter((event) => !featuredIds.has(event.id));
  return [...featured, ...rest];
}

/** Drop one calendar day, then append the fresh docs for that day. */
export function replaceEventsOnDay(events, dayKey, incoming) {
  const kept = (Array.isArray(events) ? events : []).filter(
    (event) => calendarDayKey(getEventCalendarDay(event)) !== dayKey
  );
  return appendUniqueById(kept, incoming);
}

/** Keep events outside [from, to) and replace everything inside that window. */
export function replaceEventsInWindow(events, from, to, incoming) {
  const kept = (Array.isArray(events) ? events : []).filter((event) => {
    const day = getEventCalendarDay(event);
    if (!day) return true;
    return day < from || day >= to;
  });
  return appendUniqueById(kept, incoming);
}
